import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { askGPT } from './OpenAIService';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { Link } from 'react-router-dom';
import { Lightbulb, MapPin, CheckCircle, Target, Book } from 'lucide-react';
import mathJaxConfig from './mathJaxConfig';
import ReactMarkdown from 'react-markdown';

const RecommendationsDisplay = ({ content, competenceId }) => {
  if (!content) return null;

  const sections = {
    section1: { title: "Ce que j'ai compris", content: "" },
    section2: { title: "Démarche proposée", content: "" },
    section3: { title: "Recommandations", content: "" }
  };

  // Extraction du contenu entre les balises de manière sécurisée
  content.split('\n').forEach(line => {
    const sectionMatch = line.match(/\[SECTION(\d)\]/);
    if (sectionMatch) {
      const sectionNum = sectionMatch[1];
      if (sections[`section${sectionNum}`]) {
        const nextContent = content.split(`[SECTION${sectionNum}]`)[1]?.split(/\[SECTION\d\]/)[0];
        if (nextContent) {
          sections[`section${sectionNum}`].content = nextContent.trim();
        }
      }
    }
  });

  return (
    <div className="space-y-8">
      {Object.values(sections)
        .filter(section => section.content)
        .map((section, index) => (
          <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
              {index === 0 && <Lightbulb className="w-6 h-6 mr-2 text-yellow-500" />}
              {index === 1 && <MapPin className="w-6 h-6 mr-2 text-blue-500" />}
              {index === 2 && <CheckCircle className="w-6 h-6 mr-2 text-green-500" />}
              {section.title}
            </h3>
            <div className="prose max-w-none text-gray-700">
              <MathJax>
                <ReactMarkdown>{section.content.trim()}</ReactMarkdown>
              </MathJax>
            </div>
          </div>
        ))}
      
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Prochaines étapes recommandées</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to={`/cours/${competenceId}`}
            className="bg-white rounded-lg p-4 flex flex-col items-center text-center shadow hover:shadow-md transition duration-200">
            <Book className="w-10 h-10 mb-2 text-green-500" />
            <div className="font-semibold">Espace Apprentissage</div>
            <div className="text-sm text-gray-600">Approfondissez les concepts clés</div>
          </Link>
          
          <Link to={`/inspiration/${competenceId}`}
            className="bg-white rounded-lg p-4 flex flex-col items-center text-center shadow hover:shadow-md transition duration-200">
            <Lightbulb className="w-10 h-10 mb-2 text-yellow-500" />
            <div className="font-semibold">Espace Inspiration</div>
            <div className="text-sm text-gray-600">Explorez des idées liées à vos objectifs</div>
          </Link>

          <Link to={`/training/${competenceId}`}
            className="bg-white rounded-lg p-4 flex flex-col items-center text-center shadow hover:shadow-md transition duration-200">
            <Target className="w-10 h-10 mb-2 text-blue-500" />
            <div className="font-semibold">Espace Entraînement</div>
            <div className="text-sm text-gray-600">Entraînez vos nouvelles compétences</div>
          </Link>

        </div>
      </div>
    </div>
  );
};

const Recommandations = ({ userId, onDelete }) => {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  
  const [competence, setCompetence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userGoals, setUserGoals] = useState('');
  const [userPlan, setUserPlan] = useState('');
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [tempGoal, setTempGoal] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isInitialSetup, setIsInitialSetup] = useState(true);

  const generatePlan = useCallback(async (goal) => {
    if (!goal) return;
    
    try {
      const prompt = `Tu es un coach, conseiller spécialisé en développement personnel, éducatif et professionnel.
                     Ton rôle est d'informer l'étudiant et l'aider à réaliser son projet. 
                     Adresse toi directement à l'étudiant. 

                     Format de réponse obligatoire avec balises [SECTION1], [SECTION2], [SECTION3] :

                     [SECTION1]
                     {Présente le sujet et le contexte à l'étudiant, si l'étudiant à posé une question répond à sa question, sans titre ni introduction. En 100 mots max}

                     [SECTION2]
                     {Propose une démarche pour réaliser cette action, ou cet objectif. Préciser les questions ou les orientations qui restent à définir par l'utilisateur. Si le sujet n'est pas suffisament explicite, pose une ou deux questions pour clarifier le sens et l'objectif de l'étudiant, sans titre ni introduction. en 100 mots max. }

                     [SECTION3] 
                     {Formule tes recommandations et propositions et invite l'étudiant à poursuivre dans l'espace inspiration, sans titre ni introduction, en 70 mots max}

                     Important :
                     - Utilise le markdown pour le formatage
                     - Mets les mots clés en **gras** ou *italique*
                     - Utilise $...$ pour les formules en ligne, $$...$$  pour les formules affichées

                     Voici l'objectif de l'étudiant : "${goal}"`;

      const plan = await askGPT(prompt);
      
      if (plan) {
        await updateDoc(doc(db, 'users', userId, 'competences', competenceId), {
          recommendation: plan,
          recommendationUpdatedAt: new Date().toISOString()
        });
        setUserPlan(plan);
      }
    } catch (error) {
      console.error('Erreur de génération:', error);
      setUserPlan('Erreur lors de la génération du plan.');
    }
  }, [userId, competenceId]);

  const handleSaveGoal = async () => {
    if (!competence || !tempGoal.trim()) return;
  
    try {
      setIsGeneratingPlan(true);
      const docRef = doc(db, 'users', userId, 'competences', competenceId);
      
      await updateDoc(docRef, {
        title: tempGoal,
        updatedAt: new Date().toISOString(),
        recommendation: null,
        recommendationUpdatedAt: null
      });
      
      setUserGoals(tempGoal);
      setIsEditingGoal(false);
      setCompetence(prev => ({
        ...prev,
        title: tempGoal,
        updatedAt: new Date().toISOString()
      }));
      
      await generatePlan(tempGoal);
      setShowResults(true);
      setIsInitialSetup(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const handleRegenerate = async () => {
    if (!userGoals || isGeneratingPlan || userGoals === competence.title) return;
    
    setIsGeneratingPlan(true);
    setError(null);
    const previousPlan = userPlan;
    
    try {
      setUserPlan('Génération des recommandations en cours...');
      
      const prompt = `Tu es un conseiller spécialisé en développement personnel...${userGoals}`;
      const newPlan = await askGPT(prompt);
      
      if (!newPlan) throw new Error('Aucune recommandation générée');
  
      const docRef = doc(db, 'users', userId, 'competences', competenceId);
      await updateDoc(docRef, {
        recommendation: newPlan,
        recommendationUpdatedAt: new Date().toISOString()
      });
  
      setUserPlan(newPlan);
      setCompetence(prev => ({
        ...prev,
        recommendation: newPlan,
        recommendationUpdatedAt: new Date().toISOString()
      }));
  
    } catch (error) {
      console.error('Erreur lors de la régénération:', error);
      setError('Impossible de générer de nouvelles recommandations.');
      setUserPlan(previousPlan);
    } finally {
      setIsGeneratingPlan(false);
    }
  };
  

  useEffect(() => {
    const fetchCompetence = async () => {
      if (!competenceId || !userId) {
        setError('Identifiant de compétence ou utilisateur non défini');
        setLoading(false);
        return;
      }
  
      try {
        const docSnap = await getDoc(doc(db, 'users', userId, 'competences', competenceId));
        
        if (docSnap.exists()) {
          const competenceData = { ...docSnap.data(), id: competenceId };
          setCompetence(competenceData);
          setUserGoals(competenceData.title || '');
          setTempGoal(competenceData.title || '');
          
          if (competenceData.recommendation) {
            setUserPlan(competenceData.recommendation);
            setShowResults(true);
            setIsInitialSetup(false);
          } else if (competenceData.title) {
            setShowResults(true);
            setIsInitialSetup(false);
            generatePlan(competenceData.title);
          }
        } else {
          setError('Aucune compétence trouvée.');
        }
      } catch (e) {
        console.error('Erreur de chargement:', e);
        setError('Erreur lors de la récupération de la compétence.');
      } finally {
        setLoading(false);
      }
    };
  
    setLoading(true);
    if (!auth.currentUser) {
      navigate('/');
    } else {
      fetchCompetence();
    }
  }, [competenceId, userId, navigate, generatePlan]);

  if (loading) return <div className="text-center p-8">Chargement...</div>;
  if (error) return <div className="text-red-600 p-8">{error}</div>;

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-6xl mx-auto p-6 w-full">
        <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {isInitialSetup ? "Partagez votre vision" : "Mon objectif"}
            </h2>

            {!isEditingGoal && !isInitialSetup && (
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditingGoal(true)}
                  className="p-2 hover:bg-blue-100 rounded-full transition-colors duration-200"
                  title="Modifier"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={isGeneratingPlan}
                  className={`p-2 rounded-full transition-colors duration-200 ${
                    isGeneratingPlan ? 'bg-gray-100' : 'hover:bg-green-100'
                  }`}
                  title="Régénérer les suggestions"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isGeneratingPlan ? 'text-gray-400 animate-spin' : 'text-green-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={onDelete}
                  className="p-2 hover:bg-red-100 rounded-full transition-colors duration-200"
                  title="Supprimer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {isEditingGoal || isInitialSetup ? (
            <div className="space-y-6">
              <textarea
                value={tempGoal}
                onChange={(e) => setTempGoal(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white shadow-inner"
                rows={4}
                placeholder="Décrivez votre objectif, vos aspirations, et ce que vous souhaitez accomplir..."
              />
              {isInitialSetup && (
                <div className="text-sm text-gray-500 mt-2">
                  Soyez précis et ambitieux
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveGoal}
                  disabled={!tempGoal.trim() || isGeneratingPlan}
                  className={`px-8 py-3 rounded-lg flex items-center gap-3 text-lg font-medium transition-all duration-200
                    ${tempGoal.trim() && !isGeneratingPlan
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
                      : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}
                >
                  {isGeneratingPlan ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <span>{isInitialSetup ? "Découvrir mon potentiel" : "Sauvegarder"}</span>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-lg text-gray-700 p-4 bg-white bg-opacity-50 rounded-lg border border-gray-100">
              <MathJax>
                <ReactMarkdown>{userGoals}</ReactMarkdown>
              </MathJax>
            </div>
          )}
        </div>

        {showResults && (
          <div className="mt-12">
            <div className="w-full bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Recommandations
              </h2>

              {userPlan ? (
                <RecommendationsDisplay content={userPlan} competenceId={competenceId} />
              ) : (
                <p className="text-gray-500 italic">
                  Nous préparons vos recommandations personnalisées...
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
};

export default Recommandations;